import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Users, TrendingUp, DollarSign, Copy } from 'lucide-react';
import QRCode from 'react-qr-code';
import TopBar from '../components/common/TopBar';
import BottomNav from '../components/common/BottomNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import api from '../services/api';
import { toast } from 'sonner';

const Referral = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/referral/my-code').then((r) => setData(r.data)),
      api.get('/referral/my-stats').then((r) => setStats(r.data)),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fullUrl = data ? `${window.location.origin}${data.referral_url}` : '';

  const handleShare = async () => {
    const shareData = {
      title: 'Beach Pay',
      text: data?.share_text || '🏖️ Use Beach Pay e ganhe descontos!',
      url: fullUrl,
    };
    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Compartilhado!');
      } else {
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Link copiado!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Link copiado!');
      }
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullUrl);
    toast.success('Link copiado!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar title={t('referral.title')} />
        <LoadingSpinner size="lg" />
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="referral-page">
      <TopBar title={t('referral.title')} showBack />

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Hero */}
        <div className="bg-gradient-to-br from-accent to-primary rounded-2xl p-6 text-white shadow-lg text-center">
          <div className="text-5xl mb-2">🎁</div>
          <h1 className="font-['Outfit'] text-2xl font-bold">{t('referral.title')}</h1>
          <p className="text-white/90 text-sm mt-1">{t('referral.subtitle')}</p>
        </div>

        {/* QR Code */}
        <div className="bg-white rounded-xl border border-border p-4 text-center">
          <p className="text-xs text-muted-foreground mb-2">{t('referral.yourCode')}</p>
          <div className="bg-white inline-block p-3 rounded-lg border">
            <QRCode value={fullUrl} size={160} />
          </div>
          <div className="mt-3 flex gap-2">
            <button
              data-testid="copy-referral"
              onClick={handleCopy}
              className="flex-1 py-2 bg-muted text-foreground rounded-full font-semibold text-sm flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" />
              {t('common.copy')}
            </button>
            <button
              data-testid="share-referral"
              onClick={handleShare}
              className="flex-1 py-2 bg-primary text-primary-foreground rounded-full font-semibold text-sm flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              {t('referral.shareWith')}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white rounded-xl border border-border p-3 text-center">
            <Users className="w-5 h-5 text-primary mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">{t('referral.totalReferred')}</p>
            <p className="font-bold text-lg">{stats?.total_referrals || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-3 text-center">
            <TrendingUp className="w-5 h-5 text-secondary mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">{t('referral.converted')}</p>
            <p className="font-bold text-lg">{stats?.converted || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-3 text-center">
            <DollarSign className="w-5 h-5 text-accent mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">{t('referral.earned')}</p>
            <p className="font-bold text-lg">R$ {stats?.total_cashback_earned?.toFixed(2) || '0.00'}</p>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-4">
          <h3 className="font-['Outfit'] font-bold mb-3">{t('referral.howItWorks')}</h3>
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2">
              <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
              <span>{t('referral.step1')}</span>
            </li>
            <li className="flex gap-2">
              <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
              <span>{t('referral.step2')}</span>
            </li>
            <li className="flex gap-2">
              <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
              <span>{t('referral.step3')}</span>
            </li>
          </ol>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Referral;
